import { useState } from 'react'
import { AuthUser } from 'app/provider/auth'
import { Group } from 'app/types'
import { GroupPostCreationModal } from './group-post-creation-modal'
import { PostCreationView, SubmitPostData } from './post/post-creation-view'
import { PostCreationGroupSelect } from './post-creation-group-select'
import { EventCreationView, SubmitEventData } from './event/event-creation-view'
import { Stack } from '@my/ui'
import { AnnouncementCreationView, SubmitAnnouncementData } from './announcement/announcement-creation-view'
import { UserGroupsQueryOutput } from '../../queries'
export interface GroupPostCreationWizardProps {
  open: boolean
  user: Pick<AuthUser, 'id' | 'fullName' | 'avatar' | 'token'>
  loading: boolean
  submitting: boolean
  groups: UserGroupsQueryOutput['userGroups']['docs']
  defaultGroup: Pick<Group, 'id' | 'name'> | null
  onOpenChange: (value) => void
  onSubmitPost: (data: SubmitPostData, cb: () => void) => void
  onSubmitEvent: (data: SubmitEventData, cb: () => void) => void
  onSubmitAnnouncement: (data: SubmitAnnouncementData, cb: () => void) => void
}

const pages = ['createPost', 'createEvent', 'createAnnouncement', 'privacy', 'group'] as const
export type PageType = (typeof pages)[number]

export const GroupPostCreationWizard = (props: GroupPostCreationWizardProps) => {
  const [group, setGroup] = useState<Pick<Group, 'id' | 'name'> | null>(props.defaultGroup)
  const [page, setPageType] = useState<PageType>('createPost')
  const [previousPage, setPreviousPage] = useState<PageType>('createPost')

  const formattedGroups = props.groups.map((group) => ({
    id: group.group.id,
    name: group.group.name,
    profileImage: group.group.profileImage?.sizes?.medium,
    visibility: group.group.visibility,
    totalMembers: group.group.totalMembers,
  }))

  const handleChangePage = (type: PageType) => {
    setPageType(type)
    setPreviousPage(page)
  }

  const isGroupOwner = props.groups.some((userGroup) => userGroup.group.id === group?.id && userGroup.isOwner);

  return (
    <GroupPostCreationModal open={props.open} onOpenChange={props.onOpenChange}>
      <Stack flex={1} display={page === 'createPost' ? 'flex' : 'none'}>
        <PostCreationView
          title="Create post"
          user={props.user}
          onOpenChange={props.onOpenChange}
          onSubmit={props.onSubmitPost}
          selectedGroup={group}
          onGroupSelectPress={() => handleChangePage('group')}
          onPageChange={handleChangePage}
          isSubmitting={props.submitting}
          isGroupOwner={isGroupOwner}
        />
      </Stack>

      <Stack flex={1} display={page === 'createAnnouncement' ? 'flex' : 'none'}>
        <AnnouncementCreationView
          title="Create announcement"
          user={props.user}
          onOpenChange={props.onOpenChange}
          onSubmit={props.onSubmitAnnouncement}
          selectedGroup={group}
          onGroupSelectPress={() => handleChangePage('group')}
          onBack={() => setPageType('createPost')}
          isSubmitting={props.submitting}
        />
      </Stack>

      <Stack flex={1} display={page === 'createEvent' ? 'flex' : 'none'}>
        <EventCreationView
          title="Create an event"
          user={props.user}
          onOpenChange={props.onOpenChange}
          onSubmit={props.onSubmitEvent}
          selectedGroup={group}
          onGroupSelectPress={() => handleChangePage('group')}
          onBack={() => setPageType('createPost')}
          isSubmitting={props.submitting}
        />
      </Stack>

      <Stack flex={1} display={page === 'group' ? 'flex' : 'none'}>
        <PostCreationGroupSelect
          onOpenChange={props.onOpenChange}
          onBack={() => setPageType(previousPage)}
          groups={formattedGroups}
          activeGroupId={group?.id}
          groupsLoading={props.loading}
          onGroupChange={setGroup}
        />
      </Stack>
    </GroupPostCreationModal>
  )
}
